/**
 * Copyright (c) 2023 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { gql } from '@apollo/client'

export const ProfileMediaInfo = gql`
  fragment ProfileMediaInfo on Media {
    fileName
    height
    id
    imageUrlLarge
    imageUrlMedium
    imageUrlSmall
    nsfwRating
    aiScore
    width
    ownerId
  }
`

export const ProfileBasicInfo = gql`
  fragment ProfileBasicInfo on Profile {
    id
    lastOnline
    lastVersion
    location
    name
    profileMedia {
      ...ProfileMediaInfo
    }
    supporter
    isVerified
  }
`

export const AttachedMediaInfo = gql`
  fragment AttachedMediaInfo on AttachedMedia {
    canDelete
    createdAt
    deletable
    fileName
    height
    id
    imageUrlLarge
    imageUrlMedium
    imageUrlSmall
    mediaType
    nsfwRating
    aiScore
    ownerId
    reviewerId
    reviewStatus
    videoDuration
    videoPlaybackToken
    videoThumbnailToken
    viewCount
    width
  }
`

/**
 * Chat attachments only ever render as an image or an inline video, so this selects the
 * rendering fields directly rather than reusing `MediaInfo` -- that fragment pulls
 * comment counts, like state, and permission flags that would run extra resolvers for
 * every attachment in a timeline.
 */
export const ChatMessageMediaInfo = gql`
  fragment ChatMessageMediaInfo on ChatMessageMedia {
    id
    position
    media {
      id
      fileName
      mediaType
      height
      width
      imageUrlSmall
      imageUrlMedium
      imageUrlLarge
      nsfwRating
      ownerId
      videoDuration
      videoPlaybackToken
      videoThumbnailToken
    }
  }
`

export const ChatReactionGroupInfo = gql`
  fragment ChatReactionGroupInfo on ChatReactionGroup {
    emoji
    count
    reactedByMe
  }
`

export const ChatReactionInfo = gql`
  fragment ChatReactionInfo on ChatReaction {
    id
    createdAt
    emoji
    profileId
    profile {
      ...ProfileBasicInfo
    }
  }
`

/**
 * Slim profile for the seen-by avatar stack. Kept narrow so realtime cache writes from
 * `read.changed` can seed a Profile without needing the full ProfileBasicInfo fields.
 */
export const ChatSeenByProfileInfo = gql`
  fragment ChatSeenByProfileInfo on Profile {
    id
    name
    profileMedia {
      id
      imageUrlSmall
      nsfwRating
    }
  }
`

/**
 * The quoted parent of a reply. Deliberately shallow: a reply renders as one line of
 * quoted text, and selecting `ChatMessageInfo` here would recurse without terminating.
 * The sender is narrowed to a name for the same reason -- this sits at the deepest point
 * of the timeline query, where the cost limit's depth factor is most expensive.
 */
export const ChatMessageReplyInfo = gql`
  fragment ChatMessageReplyInfo on ChatMessage {
    id
    createdAt
    deleted
    plainText
    senderId
    sender {
      id
      name
    }
  }
`

export const ChatMessageInfo = gql`
  fragment ChatMessageInfo on ChatMessage {
    id
    createdAt
    updatedAt
    content
    plainText
    edited
    editedAt
    deleted
    deletedAt
    clientMessageId
    chatRoomId
    senderId
    replyToId
    canEdit
    canDelete
    canRestrictSender
    canBanSender
    senderIsRestricted
    senderIsBanned
    seenByCount
    seenBy {
      ...ChatSeenByProfileInfo
    }
    sender {
      ...ProfileBasicInfo
    }
    replyTo {
      ...ChatMessageReplyInfo
    }
    media {
      ...ChatMessageMediaInfo
    }
    reactions {
      ...ChatReactionGroupInfo
    }
  }
`

/**
 * A member row with its profile, for the member list. Room queries
 * use the two narrower fragments below instead: spreading this one on both
 * `myMembership` and `otherMember` pulls `ProfileBasicInfo` (and the media beneath it)
 * into a room list twice per row, which is enough to trip the query cost limit.
 */
export const ChatMemberInfo = gql`
  fragment ChatMemberInfo on ChatMember {
    id
    createdAt
    chatRoomId
    profileId
    role
    lastReadAt
    lastReadMessageId
    notificationLevel
    notificationMutedUntil
    isMuted
    mutedUntil
    isRestricted
    bannedAt
    isBanned
    moderationStatus
    pinnedAt
    hiddenAt
    leftAt
    removedAt
    canChangeRole
    canRemove
    canRestrict
    canBan
    canUnban
    canTransferOwnership
    profile {
      ...ProfileBasicInfo
    }
  }
`

/**
 * The viewer's own membership. No profile: the viewer already knows who they are, and
 * selecting one here would double the depth of every row in the sidebar.
 */
export const ChatRoomMembershipInfo = gql`
  fragment ChatRoomMembershipInfo on ChatMember {
    id
    chatRoomId
    profileId
    role
    lastReadAt
    lastReadMessageId
    notificationLevel
    notificationMutedUntil
    isMuted
    mutedUntil
    isRestricted
    pinnedAt
    hiddenAt
    leftAt
    removedAt
    bannedAt
  }
`

/** The counterpart in a DM. Only ever read for its name and avatar. */
export const ChatRoomOtherMemberInfo = gql`
  fragment ChatRoomOtherMemberInfo on ChatMember {
    id
    profileId
    profile {
      ...ProfileBasicInfo
    }
  }
`

/**
 * Sidebar row preview. Kept separate from `ChatMessageInfo` so listing rooms does not
 * resolve reactions and attachments for every room's newest message.
 */
export const ChatRoomLastMessageInfo = gql`
  fragment ChatRoomLastMessageInfo on ChatMessage {
    id
    createdAt
    deleted
    plainText
    senderId
    sender {
      id
      name
    }
  }
`

export const ChatRoomInfo = gql`
  fragment ChatRoomInfo on ChatRoom {
    id
    createdAt
    updatedAt
    name
    description
    type
    isPublic
    lastMessageAt
    archivedAt
    deletedAt
    ownerId
    memberCount
    onlineCount
    hasUnread
    unreadBadgeCount
    canPost
    canEdit
    canInvite
    canModerate
    canLeave
    canJoin
    canTransfer
    canChangeRoles
    canArchive
    canDelete
    canRestore
    myMembership {
      ...ChatRoomMembershipInfo
    }
    otherMember {
      ...ChatRoomOtherMemberInfo
    }
    lastMessage {
      ...ChatRoomLastMessageInfo
    }
  }
`

/**
 * A row in the channel browser. Membership and last-message fields are omitted so the
 * list stays clear of the query cost limit; `canJoin` is enough to choose Join vs Open.
 */
export const ChatChannelInfo = gql`
  fragment ChatChannelInfo on ChatRoom {
    id
    createdAt
    name
    description
    type
    isPublic
    lastMessageAt
    archivedAt
    deletedAt
    memberCount
    onlineCount
    canJoin
  }
`

export const EventAttendeeInfo = gql`
  fragment EventAttendeeInfo on EventAttendee {
    id
    createdAt
    updatedAt
    eventId
    profileId
    peopleQty
    paymentStatus
    paymentAmount
    status
    notes
    role
    respondedAt
    profile {
      ...ProfileBasicInfo
    }
  }
`

export const EventInfo = gql`
  fragment EventInfo on Event {
    admission
    admissionCouples
    admissionMale
    admissionFemale
    admissionNote
    attendeeApproval
    allowAttendeeInvites
    attendanceCounts {
      goingCount
      maybeCount
      waitlistCount
      notGoingCount
      noneCount
    }
    joinRequestCounts {
      totalCount
      pendingCount
      approvedCount
      rejectedCount
    }
    status
    cancellationReason
    canAttend
    canPost
    canCancel
    canDelete
    canDeleteMedia
    canEdit
    canInvite
    canReport
    canSeePrivate
    canSeeAttendees
    canTransfer
    canUploadMedia
    canClone
    capacity
    createdAt
    description
    distance
    endDate
    id
    location
    locationExact
    locationLat
    locationLong
    name
    category
    ownerId
    privateDescription
    privateLocation
    privateLocationLat
    privateLocationLong
    privateAdmissionNote
    startDate
    timezone
    tagLine
    updatedAt
    visibility
    displayInFeed
    webLinks
    waitlistEnabled
    notifyWeekBefore
    notifyDayBefore
    groupCoverImageOffsetX
    groupCoverImageOffsetY
    groupCoverImageUrlSmall
    groupCoverNsfwRating
    groupId
    groupName
    slug
    tags {
      id
      name
    }
  }
`

export const EventJoinRequestInfo = gql`
  fragment EventJoinRequestInfo on EventJoinRequest {
    id
    createdAt
    updatedAt
    eventId
    profileId
    peopleQty
    notes
    eventInvitationId
    status
    respondedAt
    respondedById
    profile {
      ...ProfileBasicInfo
    }
    respondedBy {
      ...ProfileBasicInfo
    }
  }
`

export const GroupJoinRequestInfo = gql`
  fragment GroupJoinRequestInfo on GroupJoinRequest {
    id
    createdAt
    updatedAt
    groupId
    profileId
    status
    respondedAt
    respondedById
    profile {
      ...ProfileBasicInfo
    }
    respondedBy {
      ...ProfileBasicInfo
    }
  }
`

export const GroupMemberInfo = gql`
  fragment GroupMemberInfo on GroupMember {
    id
    createdAt
    groupId
    profileId
    role
    joinedAt
    invitedById
    status
    mutedUntil
    canPost
    canInvite
    profile {
      ...ProfileBasicInfo
    }
    invitedBy {
      ...ProfileBasicInfo
    }
  }
`

export const GroupInfo = gql`
  fragment GroupInfo on Group {
    id
    createdAt
    name
    description
    tagLine
    ownerId
    visibility
    groupType
    joinApproval
    postApproval
    allowMemberEvents
    allowMemberPosts
    memberCount
    postCount
    coverImage
    canJoin
    canPost
    canInvite
    canModerate
    canEdit
    canCreateEvent
    myMembership {
      ...GroupMemberInfo
    }
    owner {
      ...ProfileBasicInfo
    }
    tags {
      id
      name
    }
  }
`

export const MediaInfo = gql`
  fragment MediaInfo on Media {
    albumId
    canDelete
    canSetAsProfilePhoto
    canEdit
    caption
    commentCount
    createdAt
    deletable
    fileName
    height
    id
    imageUrlLarge
    imageUrlMedium
    imageUrlSmall
    isLiked
    likeCount
    mediaType
    nsfwRating
    aiScore
    ownerId
    reviewerId
    reviewStatus
    videoDuration
    videoPlaybackToken
    videoThumbnailToken
    viewCount
    width
  }
`

export const PartnerInfo = gql`
  fragment PartnerInfo on Partner {
    age
    bodyHair
    build
    dateOfBirth
    drinking
    education
    ethnicity
    experience
    gender
    generalInterests
    height
    id
    nickName
    profileId
    pronouns
    sexualInterests
    sexuality
    smoking
    weed
  }
`

export const PlaceInfo = gql`
  fragment PlaceInfo on Place {
    canApprove
    canDelete
    canDeleteMedia
    canEdit
    canReport
    canReview
    canTransfer
    canUploadMedia
    category
    createdAt
    description
    distance
    id
    slug
    location
    locationExact
    locationLat
    locationLong
    name
    ownerId
    phone
    reviewCount
    reviewStatus
    reviewSum
    supportersOnly
    tagLine
    updatedAt
    webLinks
    viewCount
    tags {
      id
      name
    }
  }
`

export const VendorMarketInfo = gql`
  fragment VendorMarketInfo on VendorMarket {
    canAddItem
    canApprove
    canDelete
    canDeleteMedia
    canEdit
    canReport
    canReview
    canTransfer
    canUploadMedia
    category
    createdAt
    description
    distance
    email
    id
    slug
    location
    locationLat
    locationLong
    name
    ownerId
    phone
    reviewCount
    reviewStatus
    reviewSum
    serviceRange
    tagLine
    updatedAt
    viewCount
    webLinks
  }
`

export const VendorMarketItemInfo = gql`
  fragment VendorMarketItemInfo on VendorMarketItem {
    canDelete
    canDeleteMedia
    canEdit
    canReview
    canUploadMedia
    contactForPricing
    createdAt
    description
    id
    name
    order
    ownerId
    price
    priceMax
    reviewCount
    reviewSum
    vendorMarketId
    updatedAt
  }
`

export const PostInfo = gql`
  fragment PostInfo on Post {
    approvedBy {
      ...ProfileBasicInfo
    }
    approvalStatus
    approvedAt
    canApprove
    canComment
    canDelete
    canEdit
    canReport
    commentCount
    content
    createdAt
    droolCount
    groupCoverImageOffsetX
    groupCoverImageOffsetY
    groupCoverImageUrlSmall
    groupCoverNsfwRating
    groupId
    groupName
    groupSlug
    eventId
    eventName
    eventSlug
    hahaCount
    hardCount
    id
    isEdited
    isReacted
    likeCount
    loveCount
    naughtyCount
    ownerId
    reviewStatus
    trendDetails
    trendScore
    viewCount
    wetCount
    wowCount
  }
`

export const ProfileInfo = gql`
  fragment ProfileInfo on Profile {
    about
    coverMediaUrlLarge
    createdAt
    distance
    id
    isVerified
    verificationCount
    lastOnline
    lastVersion
    likeCount
    isLiked
    isFriend
    location
    locationLat
    locationLong
    maxAge
    minAge
    name
    profileMedia {
      ...ProfileMediaInfo
    }
    relationship
    supporter
    tagLine
    viewCount
  }
`

export const PublicationInfo = gql`
  fragment PublicationInfo on Publication {
    author
    canApprove
    canDelete
    canDeleteMedia
    canEdit
    canReport
    canReview
    canTransfer
    canUploadMedia
    createdAt
    description
    id
    slug
    name
    seriesInfo
    ownerId
    reviewStatus
    reviewCount
    reviewSum
    tagLine
    updatedAt
    webLinks
    viewCount
    tags {
      id
      name
    }
  }
`

export const ReviewInfo = gql`
  fragment ReviewInfo on Review {
    id
    createdAt
    content
    markedDeleted
    rating
    parentId
    owner {
      id
      name
      supporter
      profileMedia {
        id
        reviewStatus
        imageUrlSmall
        nsfwRating
      }
    }
  }
`

export const UserInfo = gql`
  fragment UserInfo on User {
    id
    name
    email
    createdAt
    roles
    emailVerified
    roleExpiresAt
    bannedUntil
  }
`
